import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, catchError, Observable, of } from 'rxjs';

import {

  InvestigationResultDto,
  AssignCase,
  InvestigationTags,
  DeleteFileRequest,
  CreateCaseRequest,
  SimilarImageDto,
  FeatureImportance,
  EmpiricalRules,
  CaseFilterRequest,
} from '../interfaces';
import { API_URL, HUB_URL } from '../utils';
import { CoverageDto } from '../interfaces/case/CoverageDto';
import { EmpiricalRuleClaimResponse, EmpiricalRuleResponse } from '../interfaces/case/EmpiricalRule';
import { InvestigatorDto } from '../interfaces/case/InvestigatorDto';
import { AssessorsDto } from '../interfaces/case/AssessorsDto';
// import { API_URL } from '../utils/injection-tokens/api-url.token';

@Injectable({
  providedIn: 'root',
})
export class CaseService {
  readonly httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'Cache-Control':
        'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
      Pragma: 'no-cache',
      Expires: '0',
    }),
  };

  private apiUrl = inject(API_URL);
  private hubUrl = inject(HUB_URL);

  private casesSubject = new BehaviorSubject<any[]>([]);
  cases$ = this.casesSubject.asObservable();

  constructor(private http: HttpClient) {
  }


  getCases(params?: { pageNumber?: number; pageSize?: number }): Observable<any> {
    const queryParams: any = {};

    if (params?.pageNumber !== undefined) queryParams.PageNumber = params.pageNumber;
    if (params?.pageSize !== undefined) queryParams.PageSize = params.pageSize;

    return this.http.get<any>(`${this.apiUrl}Cases`, {
      ...this.httpOptions,
      params: queryParams
    });
  }

  getCaseById(caseId: string): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrl}cases/` + caseId,
      this.httpOptions
    );
  }
  // getCaseByIdV2(caseId: string): Observable<GetCaseByIdV2Response> {
  //   return this.http.get<GetCaseByIdV2Response>(
  //     `${this.apiUrl}cases/${caseId}`,
  //     this.httpOptions
  //   );
  // }
  getCaseInvestigationResultsById(
    caseId: string
  ): Observable<InvestigationResultDto[]> {
    return this.http.get<InvestigationResultDto[]>(
      `${this.apiUrl}case/${caseId}/investigationResults`,
      this.httpOptions
    );
  }

  getFeatureImportanceById(fraudDataId: number): Observable<FeatureImportance> {
    return this.http.get<FeatureImportance>(
      `${this.apiUrl}FeatureImportance/` + fraudDataId,
      this.httpOptions
    );
  }

  getEmpiricalRulesByClaimId(
    claimId: string
  ): Observable<EmpiricalRuleResponse[]> {
    return this.http.get<EmpiricalRuleResponse[]>(
      `${this.apiUrl}empiricalRule/${claimId}`,
      this.httpOptions
    ).pipe(catchError((err) => {
      console.error(err)
      return of([])
    }));
  }

  getActiveEmpiricalRulesByClaimId(
    claimId: string
  ): Observable<EmpiricalRuleClaimResponse[]> {
    return this.http.get<EmpiricalRuleClaimResponse[]>(
      `${this.apiUrl}empiricalRuleClaim/active/${claimId}`,
      this.httpOptions
    ).pipe(catchError((err) => {
      console.error(err)
      return of([])
    }));
  }

  getEmpiricalRulesById(
    fraudDataId: number
  ): Observable<EmpiricalRules[]> {
    return this.http.get<EmpiricalRules[]>(
      `${this.apiUrl}EmpiricalRulesV2/` + fraudDataId,
      this.httpOptions
    );
  }

  /**
   * Updates the status of a case.
   * Changes the case status from "New" to "Old" and from "Not Investigated" to "Investigated".
   *
   * @param caseId - The unique identifier of the case.
   * @param patchCase - The object containing the updated case details.
   */
  patchCase(caseId: string, patchCase: IPatchCase[]): Observable<any> {
    const headers = new HttpHeaders({
      'Content-Type': 'application/json-patch+json',
    });
    return this.http.patch(`${this.apiUrl}cases/${caseId}`, patchCase, {
      headers,
    });
  }

  assignToCase(request: AssignCase): Observable<AssignCase> {
    return this.http.put<AssignCase>(
      `${this.apiUrl}cases/assign`,
      request,
      this.httpOptions
    );
  }

  getInvestigationTags(): Observable<InvestigationTags[]> {
    return this.http.get<InvestigationTags[]>(
      `${this.apiUrl}investigation-tags/`,
      this.httpOptions
    );
  }

  updateApprovalStatus(caseId: string, caseStatus: any) {
    /**
     *
     * caseStatus == 0 for reject
     * caseStatus == 1 for approve
     */

    // {
    //   caseStatus:0/1,
    //   assigneeId:requestUserId,
    // }
    return this.http.put(
      `${this.apiUrl}case/${caseId}/approval-status`,
      caseStatus,
      this.httpOptions
    );
  }

  //CoReact service methods

  getImagesByClaimNumber(claimNumber: string): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrl}imagesV2/` + claimNumber,
      this.httpOptions
    );
  }

  getImageByVehicleId(
    fraudDataId: number,
    vehicleId: number,
    imageName: string
  ): Observable<Blob> {
    return this.http.get<Blob>(
      `${this.hubUrl}/document/` + fraudDataId + '/' + vehicleId + '/' + imageName,
      {
        responseType: 'blob' as 'json',
      }
    );
  }

  getSimilarImagesById(fraudDataId: number): Observable<SimilarImageDto[]> {
    return this.http.get<SimilarImageDto[]>(
      `${this.apiUrl}CaseSimilarImages/` + fraudDataId,
      this.httpOptions
    );
  }

  getHistoricCaseDataByCaseId(caseId: string): Observable<any> {
    return this.http.get<any>(
      `${this.apiUrl}historic-data?caseId=${caseId}`,
      this.httpOptions
    );
  }

  createInvestigatorResult(caseId: string, comment: FormData): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}cases/${caseId}/create-investigation-result`,
      comment
    );
  }
  updateInvestigationStatus(investigationResultId: string): Observable<any> {
    return this.http.put<any>(
      `${this.apiUrl}case/investigation-result/${investigationResultId}`,
      this.httpOptions
    );
  }

  downloadCaseFiles(
    caseId: string,
    fileName: string
  ): Observable<Blob> {
    const url = `${this.apiUrl}cases/${caseId}/download?fileName=${encodeURIComponent(fileName)}`;
    return this.http.get(url, { responseType: 'blob' })
    //  {
    //   responseType: 'blob' as 'json', // Cast responseType properly
    // });
  }

  deleteInvestigationResultTag(investigationResultId: string, tagId: number) {
    return this.http.delete<any>(
      `${this.apiUrl}investigationResults/${investigationResultId}/tag/${tagId}`
    );
  }
  addInvestigationResultTag(investigationResultId: string, tags: string[]) {
    return this.http.post<any>(
      `${this.apiUrl}investigationResults/${investigationResultId}/tags`,
      tags
    );
  }

  updateInvestigationResultComment(
    investigationResultId: string,
    investigationResultComment: string
  ) {
    return this.http.put<any>(
      `${this.apiUrl}investigationResults/${investigationResultId}/comment`,
      { comment: investigationResultComment }
    );
  }

  deleteInvestigationResultComment(investigationResultId: string) {
    return this.http.delete<any>(
      `${this.apiUrl}investigationResults/${investigationResultId}/comment`
    );
  }


  deleteInvestigationResultFile(deleteFile: DeleteFileRequest) {
    return this.http.delete<any>(`${this.apiUrl}files`, {
      body: deleteFile,
    });
  }
  uploadFile(uploadFile: FormData): Observable<void> {
    return this.http.post<any>(`${this.apiUrl}files`, uploadFile);
  }

  createCase(newCase: CreateCaseRequest) {
    return this.http.post<any>(`${this.apiUrl}fraudData/new/form`, newCase);
  }
  importCasesFromFile(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const headers = new HttpHeaders({
      enctype: 'multipart/form-data',
    });

    return this.http.post(`${this.apiUrl}fraudData/new/flatFile`, formData, {
      headers,
    });
  }
  importInvoicesFromFile(file: File): Observable<any> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const headers = new HttpHeaders({
      enctype: 'multipart/form-data',
    });

    return this.http.post(`${this.apiUrl}fraudData/invoices`, formData, {
      headers,
    });
  }
  getDistinctInsuranceCoverages(): Observable<CoverageDto[]> {
    return this.http.get<CoverageDto[]>(`${this.apiUrl}cases/distinct-insurance-coverages`);
  }
    addSavedAmount(caseId: string, savedAmount: number) {
  return this.http.post(`${this.apiUrl}cases/${caseId}/savedAmount?savedAmount=${savedAmount}`, {});
}
  getInvestigators(): Observable<InvestigatorDto[]> {
    return this.http.get<InvestigatorDto[]>(`${this.apiUrl}cases/investigators`);
  }
  getAssessors(): Observable<AssessorsDto[]> {
    return this.http.get<AssessorsDto[]>(`${this.apiUrl}cases/assessors`);
  }
  getAssessorsByClaimId(claimId: string): Observable<AssessorsDto[]> {
    return this.http.get<AssessorsDto[]>(`${this.apiUrl}cases/assessors/${claimId}`);
  }

  createViewedCase(caseId: string): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}cases/${caseId}/viewed`,
      {}
    );
  }
}
export interface IPatchCase {
  op: 'replace';
  path: '/isNewCase' | '/isInvestigated';
  value: boolean;
}
